import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Copy, Mail, MessageCircle, Send } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { APP_URL } from "@/lib/constants";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  DrawerClose,
} from "@/components/ui/drawer";

interface ProfileInviteDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  profile: any;
}

export const ProfileInviteDrawer = ({ isOpen, onClose, profile }: ProfileInviteDrawerProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation('profile');
  const [email, setEmail] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  // Handle case where referral_code is undefined
  const referralCode = profile?.referral_code;

  const displayName = profile?.first_name && profile?.last_name
    ? `${profile.first_name} ${profile.last_name}`
    : profile?.username || '';

  // Generate simple invite link using referral code (same as personal invites)
  const inviteLink = referralCode ? `${APP_URL}/invite?ref=${referralCode}` : "";

  // Load default message when component opens
  useEffect(() => {
    if (isOpen && displayName) {
      if (inviteLink) {
        const defaultMessage = t('invite.defaultMessage', {
          profileName: displayName,
          link: inviteLink
        });
        setCustomMessage(defaultMessage);
      } else {
        // Fallback message when no referral link is available
        const fallbackMessage = t('invite.fallbackMessage', {
          profileName: displayName
        });
        setCustomMessage(fallbackMessage);
      }
    }
  }, [isOpen, inviteLink, displayName, t]);

  const handleCopyMessageAndLink = async () => {
    if (!customMessage.trim()) {
      toast({
        title: "Error",
        description: "Please enter a message to copy",
        variant: "destructive",
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(customMessage);
      toast({
        title: t('invite.messageCopied'),
      });
    } catch (error) {
      toast({
        title: "Failed to copy message",
        variant: "destructive",
      });
    }
  };

  const handleCopyLink = async () => {
    if (!inviteLink) {
      toast({
        title: "Error",
        description: t('invite.notAvailable'),
        variant: "destructive",
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(inviteLink);
      toast({
        title: t('invite.linkCopied'),
      });
    } catch (error) {
      toast({
        title: "Failed to copy link",
        variant: "destructive",
      });
    }
  };

  const handleWhatsAppShare = () => {
    if (!customMessage.trim()) {
      toast({
        title: "Error",
        description: "Please enter a message to share",
        variant: "destructive",
      });
      return;
    }

    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(customMessage)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleTelegramShare = () => {
    if (!customMessage.trim()) {
      toast({
        title: "Error",
        description: "Please enter a message to share",
        variant: "destructive",
      });
      return;
    }

    const telegramUrl = inviteLink 
      ? `https://t.me/share/url?url=${encodeURIComponent(inviteLink)}&text=${encodeURIComponent(customMessage)}`
      : `https://t.me/share/url?text=${encodeURIComponent(customMessage)}`;
    window.open(telegramUrl, '_blank');
  };

  const handleSendInvite = async () => {
    if (!email.trim()) {
      toast({
        title: "Email required",
        description: "Please enter an email address",
        variant: "destructive",
      });
      return;
    }

    if (!referralCode) {
      toast({
        title: "Error",
        description: "Cannot send invite - referral code is not available",
        variant: "destructive",
      });
      return;
    }

    setIsSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-friend-invite', {
        body: {
          inviterName: profile.first_name && profile.last_name 
            ? `${profile.first_name} ${profile.last_name}` 
            : profile.username,
          inviterEmail: user?.email,
          inviteeEmail: email,
          inviteMessage: emailMessage.trim() || undefined,
          referralCode: referralCode,
          invitingProfileId: profile.id
        }
      });

      if (error) throw error;

      toast({
        title: "Invitation sent!",
        description: `Invitation sent to ${email} from ${profile.username}`,
      });

      setEmail("");
      setEmailMessage("");
      onClose();
    } catch (error) {
      console.error('Error sending invitation:', error);
      toast({
        title: "Failed to send invitation",
        description: "Please try again later",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent>
        <DrawerHeader className="text-center">
          <DrawerTitle>{t('invite.title', { profileName: displayName })}</DrawerTitle>
          <DrawerDescription>
            {t('invite.description')}
          </DrawerDescription>
        </DrawerHeader>

        <div className="px-4 pb-4 space-y-6">
          {/* Custom Message Section */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">{t('invite.customMessage')}</Label>
            <Textarea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder={t('invite.customMessagePlaceholder')}
              rows={4}
              className="resize-none"
            />
            <div className="flex gap-2">
              <Button
                onClick={handleCopyMessageAndLink}
                disabled={!customMessage.trim()}
                className="gap-2 flex-1"
              >
                <Copy className="h-4 w-4" />
                {t('invite.copyMessageLink')}
              </Button>
            </div>
          </div>

          {/* Enhanced Social Sharing */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">{t('invite.quickShare')}</Label>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleWhatsAppShare}
                className="gap-2 text-green-600"
                disabled={!customMessage.trim()}
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleTelegramShare}
                className="gap-2 text-blue-600"
                disabled={!customMessage.trim()}
              >
                <Send className="h-4 w-4" />
                Telegram
              </Button>
            </div>
          </div>

          {/* Referral Link Section */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t('invite.referralLink')}</Label>
            {inviteLink ? (
              <div className="flex gap-2">
                <Input
                  value={inviteLink}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyLink}
                  className="gap-2"
                >
                  <Copy className="h-4 w-4" />
                  {t('invite.copyLinkOnly')}
                </Button>
              </div>
            ) : (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  {t('invite.notAvailable')}
                </p>
              </div>
            )}
          </div>

          {/* Email Invitation */}
          <div className="space-y-4">
            <Label className="text-sm font-medium">{t('invite.sendEmailInvitation')}</Label>
            <div className="space-y-3">
              <div>
                <Label htmlFor="email" className="text-sm">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="friend@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="emailMessage" className="text-sm">{t('invite.personalMessage')}</Label>
                <Textarea
                  id="emailMessage"
                  placeholder={t('invite.customMessagePlaceholder')}
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  rows={3}
                />
              </div>
              <Button
                onClick={handleSendInvite}
                disabled={isSending || !referralCode}
                className="w-full gap-2"
              >
                <Mail className="h-4 w-4" />
                {isSending ? 'Sending...' : 'Send Invitation'}
              </Button>
            </div>
          </div>
        </div>

        <DrawerFooter>
          <DrawerClose asChild>
            <Button variant="outline" className="w-full">
              Close
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};